import React, { useEffect, ReactElement } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";

// Argon Dashboard 2 MUI example components
import SidenavItem from "themeComponents/Sidenav/SidenavItem";
import SidenavFooter from "themeComponents/Sidenav/SidenavFooter";

// Custom styles for the Sidenav
import SidenavRoot from "themeComponents/Sidenav/SidenavRoot";
import sidenavLogoLabel from "themeComponents/Sidenav/styles/sidenav";

// Argon Dashboard 2 MUI context
import { useArgonController, setMiniSidenav } from "context";
import { useSelector } from "react-redux";
import { RootState } from "configs/store";
import useAuth from "hooks/useAuth";

interface RouteItem {
  type: "route" | "title" | "divider";
  name?: string;
  icon?: ReactElement | string;
  title?: string;
  key: string;
  href?: string;
  route?: string;
  hideFromSidebar?: boolean;
  noCollapse?: boolean;
}

interface SidenavProps {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: RouteItem[];
  [key: string]: any;
}

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }: SidenavProps): React.JSX.Element {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkSidenav, layout } = controller;
  const user = useSelector((state: RootState) => state.auth.user);
  const { isAdmin, isCompanyAdmin, isFellowAdmin, isCompany, isCreator, isHR, isManager } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const isAdminEnrollmentsView = isAdmin || isCompanyAdmin || isFellowAdmin || isCompany || isHR || isManager;
  const hideEnrollments = isCreator;
  const canSeeExploreCourses = isAdmin || isCompanyAdmin || isFellowAdmin || isCompany || isCreator || isHR || isManager;

  const normalizePath = (path: string): string => {
    if (!path) return "/";
    const cleaned = path.replace(/\/+$/, "");
    return cleaned === "" ? "/" : cleaned;
  };

  const routePatternToRegex = (route: string): RegExp => {
    const escaped = route
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\\:[^/]+\\\?/g, "[^/]+")
      .replace(/\\:[^/]+/g, "[^/]+");
    return new RegExp(`^${escaped}$`, "i");
  };

  const normalizedPath = normalizePath(pathname);
  const activeRouteKey = routes
    .filter((route) => route.type === "route" && !!route.route)
    .sort((a, b) => (b.route?.length || 0) - (a.route?.length || 0))
    .find((route) => route.route && routePatternToRegex(normalizePath(route.route)).test(normalizedPath))?.key;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav(): void {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, key, href, route, hideFromSidebar, noCollapse }) => {
    const computedHidden =
      hideFromSidebar ||
      (key === "my-enrollments" && hideEnrollments) ||
      (key === "explore-courses" && !canSeeExploreCourses) ||
      (key === "creators" && !isAdmin);
    const computedName = key === "my-enrollments" ? (isAdminEnrollmentsView ? "Enrollments" : "My Enrollments") : name;
    let returnValue: ReactElement | null = null;

    if (type === "route" && !computedHidden) {
      if (href) {
        returnValue = (
          <Link href={href} key={key} target="_blank" rel="noreferrer">
            <SidenavItem
              name={computedName || ""}
              icon={icon}
              active={key === activeRouteKey}
              noCollapse={noCollapse}
              open={false}
            />
          </Link>
        );
      } else {
        returnValue = (
          <NavLink to={route || "#"} key={key} end>
            <SidenavItem name={computedName || ""} icon={icon} active={key === activeRouteKey} open={false} />
          </NavLink>
        );
      }
    } else if (type === "title" && !computedHidden) {
      returnValue = (
        <Typography
          key={key}
          color={darkSidenav ? "white" : "dark"}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </Typography>
      );
    } else if (type === "divider" && !computedHidden) {
      returnValue = <Divider key={key} light={darkSidenav} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" {...({ ownerState: { darkSidenav, miniSidenav, layout } } as any)}>
      <Box pt={3} pb={1} px={4} textAlign="center">
        <Box
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <Typography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </Typography>
        </Box>
        <Box component={NavLink as any} to="/" display="flex" alignItems="center" {...({} as any)}>
          {brand && (
            <Box component="img" src={brand} alt="Argon Logo" width="3rem" mr={0.25} {...({} as any)} />
          )}
          <Box
            width={!brandName ? "100%" : undefined}
            sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <Typography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={darkSidenav ? "white" : "dark"}
            >
              {brandName}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider light={darkSidenav} />
      <List>{renderRoutes}</List>

      <Box pt={1} mt="auto" mb={2} mx={2}>
        <SidenavFooter />
      </Box>
    </SidenavRoot>
  );
}

export default Sidenav;

